import { useEffect, useMemo, useRef, useState } from "react"

import type {
  ActivityMessage,
  ThoroughSearchGraphActivityMessage,
  ThoroughSearchGraphEvent,
} from "@/api/models"

import { GitBranch } from "@/components/icons"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { cn } from "@/lib/utils"

type TreeGraphPanelProps = {
  messages: ActivityMessage[]
}

type GraphNodeState = {
  id: string
  label: string
  status: "idle" | "active" | "selected" | "merged" | "complete" | "failed"
  parentId?: string | null
  depth?: number
}

type PositionedNode = GraphNodeState & {
  x: number
  y: number
}

/* ── Layout constants ── */
const GRAPH_WIDTH = 720
const GRAPH_HEIGHT = 200
const PAD_X = 60
const PAD_Y_TOP = 30
const PAD_Y_BOTTOM = 30
const MAX_CHILDREN = 6

const getNodeId = (event?: ThoroughSearchGraphEvent): string | undefined => {
  if (!event) return undefined
  if (event.type === "choose_child") return event.child_id ?? event.node_id
  return event.node_id
}

const isSyntheticCandidateLabel = (label: string): boolean =>
  /^candidate-\d+$/i.test(label) || /^selected-\d+$/i.test(label)

const truncateLabel = (label: string, max: number): string => {
  if (label.length <= max) return label
  return `${label.slice(0, max - 1)}…`
}

export function TreeGraphPanel({ messages }: TreeGraphPanelProps) {
  const graphMessages = useMemo(
    () =>
      messages.filter(
        (message): message is ThoroughSearchGraphActivityMessage =>
          message.activity_type === "thorough_search_graph"
      ),
    [messages]
  )

  /* ── Animation / playback ── */
  const [playbackIndex, setPlaybackIndex] = useState(0)
  const playbackTimerRef = useRef<number | null>(null)
  const playbackTargetRef = useRef(0)
  // Track the message count at initial mount so we can skip animation for
  // messages that were already present (e.g. re-opening the panel).
  const initialCountRef = useRef<number | null>(null)

  useEffect(() => {
    // On first render, snapshot how many messages already exist.
    if (initialCountRef.current === null) {
      initialCountRef.current = graphMessages.length
      // If messages already exist (re-open), jump straight to the end.
      if (graphMessages.length > 0) {
        setPlaybackIndex(graphMessages.length)
        return
      }
    }

    playbackTargetRef.current = graphMessages.length
    if (graphMessages.length === 0) {
      if (playbackTimerRef.current) {
        window.clearInterval(playbackTimerRef.current)
        playbackTimerRef.current = null
      }
      setPlaybackIndex(0)
      return
    }
    setPlaybackIndex((prev) => {
      if (prev === 0) return 1
      return Math.min(prev, graphMessages.length)
    })
    if (!playbackTimerRef.current) {
      playbackTimerRef.current = window.setInterval(() => {
        setPlaybackIndex((prev) => {
          const target = playbackTargetRef.current
          if (prev >= target) {
            if (playbackTimerRef.current) {
              window.clearInterval(playbackTimerRef.current)
              playbackTimerRef.current = null
            }
            return prev
          }
          return prev + 1
        })
      }, 320) // slower tick — fewer nodes to animate
    }
  }, [graphMessages.length])

  useEffect(() => {
    return () => {
      if (playbackTimerRef.current) {
        window.clearInterval(playbackTimerRef.current)
        playbackTimerRef.current = null
      }
    }
  }, [])

  const playbackMessages = useMemo(
    () => graphMessages.slice(0, Math.max(0, playbackIndex)),
    [graphMessages, playbackIndex]
  )

  const traversalMode = useMemo(() => {
    const explicitMode = [...playbackMessages]
      .reverse()
      .map((message) => message.details?.traversal_mode)
      .find((mode): mode is "real" | "fallback" => mode === "real" || mode === "fallback")

    if (explicitMode) return explicitMode

    const hasTreeFailed = playbackMessages.some(
      (message) => message.details?.event?.type === "tree_failed"
    )
    return hasTreeFailed ? "fallback" : "real"
  }, [playbackMessages])

  /* ── Build node map from playback messages ── */
  const { nodeMap, childOrderByParent, activeNodeId } = useMemo(() => {
    const nextNodeMap = new Map<string, GraphNodeState>()
    const nextChildOrderByParent = new Map<string, string[]>()
    let nextActiveNodeId: string | undefined

    const resolveNodeId = (candidate: string | undefined): string | undefined => {
      if (!candidate) return undefined
      if (candidate !== "root") return candidate
      if (nextNodeMap.has("root")) return "root"
      const knownRoots = Array.from(nextNodeMap.values()).filter((node) => !node.parentId)
      if (knownRoots.length === 1) return knownRoots[0].id
      return candidate
    }

    for (const message of playbackMessages) {
      const event = message.details?.event as ThoroughSearchGraphEvent | undefined
      const nodeId = resolveNodeId(getNodeId(event))
      if (!event || !nodeId) continue

      const existing = nextNodeMap.get(nodeId)
      const labelFromEvent = event.type === "enter_node" ? event.label : undefined
      const node =
        existing ??
        ({
          id: nodeId,
          label: labelFromEvent || nodeId,
          status: "idle",
        } satisfies GraphNodeState)

      if (event.type === "enter_node") {
        node.status = "active"
        node.parentId = event.parent_id
        node.depth = event.depth
        node.label = event.label || node.label
        nextActiveNodeId = nodeId
      }

      if (event.type === "evaluate_children") {
        const parentId = resolveNodeId(event.node_id)
        if (!parentId) continue
        const orderedChildIds = (event.child_ids ?? []).slice(0, MAX_CHILDREN)
        nextChildOrderByParent.set(parentId, orderedChildIds)
        for (const childId of orderedChildIds) {
          const existingChild = nextNodeMap.get(childId)
          const childNode =
            existingChild ??
            ({
              id: childId,
              label: childId,
              status: "idle",
            } satisfies GraphNodeState)
          if (!childNode.parentId) childNode.parentId = parentId
          nextNodeMap.set(childId, childNode)
        }
      }

      if (event.type === "choose_child") {
        node.status = "selected"
        const resolvedParentId = resolveNodeId(event.node_id)
        if (!node.parentId && resolvedParentId && resolvedParentId !== node.id) {
          node.parentId = resolvedParentId
        }
        const selectedTitle = event.evidence?.[0]?.title
        const selectedSource = event.evidence?.[0]?.source
        if (selectedTitle && typeof selectedTitle === "string") {
          node.label = selectedTitle
        } else if (
          selectedSource &&
          typeof selectedSource === "string" &&
          isSyntheticCandidateLabel(node.label)
        ) {
          node.label = selectedSource
        }
        nextActiveNodeId = nodeId
      }

      if (event.type === "merge_results") node.status = "merged"
      if (event.type === "final_context_ready") node.status = "complete"
      if (event.type === "tree_failed") node.status = "failed"

      if (event.type === "retrieve_leaf_text" && event.source) {
        if (isSyntheticCandidateLabel(node.label)) {
          node.label = String(event.source)
        }
      }
      nextNodeMap.set(nodeId, node)
    }

    return {
      nodeMap: nextNodeMap,
      childOrderByParent: nextChildOrderByParent,
      activeNodeId: nextActiveNodeId,
    }
  }, [playbackMessages])

  if (graphMessages.length === 0) return null

  /* ── Derived graph structures ── */
  const nodes = Array.from(nodeMap.values())
  const nodeById = new Map(nodes.map((node) => [node.id, node]))

  const canonicalRootId = (() => {
    if (nodeById.has("root")) return "root"
    const depthZeroNode = nodes.find((node) => node.depth === 0)
    if (depthZeroNode) return depthZeroNode.id
    const rootCandidate = nodes.find((node) => !node.parentId || !nodeById.has(node.parentId))
    return rootCandidate?.id
  })()

  const childrenByParent = (() => {
    const map = new Map<string, string[]>()
    const appendChild = (parentId: string, childId: string) => {
      const current = map.get(parentId) ?? []
      if (current.includes(childId)) return
      if (current.length >= MAX_CHILDREN) return
      current.push(childId)
      map.set(parentId, current)
    }
    for (const [parentId, orderedChildIds] of childOrderByParent.entries()) {
      for (const childId of orderedChildIds) appendChild(parentId, childId)
    }
    for (const node of nodes) {
      if (!node.parentId) continue
      appendChild(node.parentId, node.id)
    }
    return map
  })()

  /* ── Layout computation ── */
  const graphView = (() => {
    if (!canonicalRootId) {
      return {
        positionedNodes: [] as PositionedNode[],
        edges: [] as Array<{ parentId: string; childId: string }>,
        activePathNodeIds: new Set<string>(),
        activeEdgeIds: new Set<string>(),
      }
    }

    const reachable = new Set<string>()
    const nodeLayoutInfo = new Map<string, { depth: number }>()
    let maxDepth = 0

    // BFS to assign depth
    const bfsQueue: Array<{ nodeId: string; depth: number }> = [
      { nodeId: canonicalRootId, depth: 0 },
    ]
    while (bfsQueue.length > 0) {
      const item = bfsQueue.shift()!
      if (reachable.has(item.nodeId)) continue
      const node = nodeById.get(item.nodeId)
      if (!node) continue
      reachable.add(item.nodeId)
      nodeLayoutInfo.set(item.nodeId, { depth: item.depth })
      maxDepth = Math.max(maxDepth, item.depth)
      const children = (childrenByParent.get(item.nodeId) ?? []).slice(0, MAX_CHILDREN)
      for (const childId of children) {
        bfsQueue.push({ nodeId: childId, depth: item.depth + 1 })
      }
    }

    // Group nodes by depth
    const nodesPerDepth = new Map<number, string[]>()
    for (const nodeId of reachable) {
      const info = nodeLayoutInfo.get(nodeId)
      if (!info) continue
      const list = nodesPerDepth.get(info.depth) ?? []
      list.push(nodeId)
      nodesPerDepth.set(info.depth, list)
    }

    const usableWidth = GRAPH_WIDTH - PAD_X * 2
    const usableHeight = GRAPH_HEIGHT - PAD_Y_TOP - PAD_Y_BOTTOM
    const levelCount = maxDepth + 1

    const positionedNodes: PositionedNode[] = []
    const edges: Array<{ parentId: string; childId: string }> = []

    for (const nodeId of reachable) {
      const node = nodeById.get(nodeId)
      const info = nodeLayoutInfo.get(nodeId)
      if (!node || !info) continue
      const depthNodes = nodesPerDepth.get(info.depth) ?? [nodeId]
      const indexInLevel = depthNodes.indexOf(nodeId)
      const countAtLevel = depthNodes.length
      const tX = (indexInLevel + 0.5) / Math.max(1, countAtLevel)
      const x = PAD_X + tX * usableWidth
      const y =
        levelCount <= 1
          ? PAD_Y_TOP + usableHeight / 2
          : PAD_Y_TOP + (info.depth / (levelCount - 1)) * usableHeight
      positionedNodes.push({ ...node, x, y })
      const children = (childrenByParent.get(nodeId) ?? []).slice(0, MAX_CHILDREN)
      for (const childId of children) {
        if (reachable.has(childId)) edges.push({ parentId: nodeId, childId })
      }
    }

    const activePathNodeIds = new Set<string>()
    if (activeNodeId && reachable.has(activeNodeId)) {
      let cursor: string | undefined = activeNodeId
      while (cursor) {
        const current = nodeById.get(cursor)
        if (!current || activePathNodeIds.has(cursor)) break
        activePathNodeIds.add(cursor)
        cursor = current.parentId ?? undefined
      }
    }

    const activeEdgeIds = new Set<string>()
    for (const edge of edges) {
      if (activePathNodeIds.has(edge.parentId) && activePathNodeIds.has(edge.childId)) {
        activeEdgeIds.add(`${edge.parentId}->${edge.childId}`)
      }
    }

    return { positionedNodes, edges, activePathNodeIds, activeEdgeIds }
  })()

  /* ── Color helpers ── */
  const nodeColor = (node: PositionedNode, isOnActivePath: boolean): string => {
    if (isOnActivePath) return "hsl(var(--success))"
    const depth = node.depth ?? 0
    // Root = blue, doc branches = teal, summary leaves = green/amber
    if (depth === 0) return "hsl(var(--chart-1))"
    if (depth === 1) return "hsl(var(--chart-2))"
    // summary leaf: color by evidence count
    if (node.label.includes("no results")) return "hsl(var(--destructive))"
    return "hsl(var(--chart-4))"
  }

  const nodeRadius = (node: PositionedNode, isActive: boolean): number => {
    if (isActive) return 10
    const depth = node.depth ?? 0
    if (depth === 0) return 9
    if (depth === 1) return 8
    return 7
  }

  return (
    <Card className="border-0 bg-transparent shadow-none">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <GitBranch className="text-muted-foreground size-4" />
          <span>Reasoning Tree</span>
          <span
            className={cn(
              "rounded-full px-2 py-0.5 text-xs font-medium",
              traversalMode === "real"
                ? "bg-success/15 border-success text-foreground border"
                : "bg-warning/30 text-warning-ink"
            )}
          >
            {traversalMode === "real" ? "Real traversal" : "Fallback traversal"}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="bg-muted/10 overflow-x-auto rounded-md p-2">
          {graphView.positionedNodes.length > 0 ? (
            <svg
              aria-label="Tree traversal visualization"
              className="w-full"
              role="img"
              style={{ height: `${GRAPH_HEIGHT}px` }}
              viewBox={`0 0 ${GRAPH_WIDTH} ${GRAPH_HEIGHT}`}
            >
              {/* Edges */}
              {graphView.edges.map((edge) => {
                const parent = graphView.positionedNodes.find((n) => n.id === edge.parentId)
                const child = graphView.positionedNodes.find((n) => n.id === edge.childId)
                if (!parent || !child) return null
                const edgeId = `${edge.parentId}->${edge.childId}`
                const isActiveEdge = graphView.activeEdgeIds.has(edgeId)
                // Curved edges for a cleaner look
                const midY = (parent.y + child.y) / 2
                return (
                  <path
                    key={edgeId}
                    d={`M ${parent.x} ${parent.y} C ${parent.x} ${midY}, ${child.x} ${midY}, ${child.x} ${child.y}`}
                    fill="none"
                    opacity={isActiveEdge ? 0.9 : 0.4}
                    strokeLinecap="round"
                    strokeWidth={isActiveEdge ? 2.5 : 1.5}
                    className={cn(
                      "transition-[stroke,stroke-width,opacity] duration-500",
                      isActiveEdge ? "stroke-primary" : "stroke-muted-foreground/30"
                    )}
                  />
                )
              })}

              {/* Nodes */}
              {graphView.positionedNodes.map((node) => {
                const isActiveNode = node.id === activeNodeId
                const isOnActivePath = graphView.activePathNodeIds.has(node.id)
                const fill = nodeColor(node, isOnActivePath)
                const r = nodeRadius(node, isActiveNode)
                const depth = node.depth ?? 0

                // Label positioning: root label below, others to the right
                const labelX = depth === 0 ? node.x : node.x + r + 6
                const labelY = depth === 0 ? node.y + r + 14 : node.y + 4
                const textAnchor = depth === 0 ? "middle" : "start"
                const maxCharsMap: Record<number, number> = { 0: 20, 1: 30 }
                const maxChars = maxCharsMap[depth] ?? 18

                return (
                  <g key={node.id}>
                    <title>{node.label}</title>
                    {/* Subtle glow behind active node */}
                    {isActiveNode && (
                      <circle
                        className="animate-pulse"
                        cx={node.x}
                        cy={node.y}
                        fill={fill}
                        opacity={0.2}
                        r={r + 6}
                        stroke="hsl(var(--foreground))"
                        strokeWidth={1}
                      />
                    )}
                    <circle
                      className="transition-[fill,stroke,opacity,r] duration-500"
                      cx={node.x}
                      cy={node.y}
                      fill={fill}
                      opacity={0.95}
                      r={r}
                      stroke="hsl(var(--foreground))"
                      strokeWidth={1}
                    />
                    <text
                      style={{ fontSize: depth === 0 ? "12px" : "11px" }}
                      textAnchor={textAnchor}
                      x={labelX}
                      y={labelY}
                      className={cn(
                        "fill-foreground/80 transition-colors duration-300",
                        isActiveNode && "fill-primary font-semibold"
                      )}
                    >
                      {truncateLabel(node.label, maxChars)}
                    </text>
                  </g>
                )
              })}
            </svg>
          ) : (
            <p className="text-muted-foreground text-sm">
              Tree updates received, waiting for node details…
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
